import { PlusOutlined } from "@ant-design/icons"
import { Button, Flex, Modal, Typography } from "antd"
import React, { useCallback, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"

import { nextPageUrl } from "../../redux/shared/paged-list.ts"
import type { RootState } from "../../store"
import { extractParamsFromNextUrl } from "../../utils/form/extract-params-from-next-url.ts"
import type { CreateSupplierData } from "./api/services.types.ts"
import { SupplierFormModal } from "./components/SupplierFormModal.tsx"
import { SuppliersList } from "./components/SuppliersList.tsx"
import {
  suppliersClearDetails,
  suppliersClearSaveResult,
  suppliersSetFilters
} from "./redux/suppliers.slice.ts"
import {
  createSupplier,
  deleteSupplier,
  fetchSupplierDetails,
  fetchSuppliers,
  updateSupplier
} from "./redux/suppliers.thunks.ts"
import type { SuppliersFilters } from "./redux/suppliers.types.ts"
import type { SupplierListItem } from "./types/types.ts"

const { Title, Text } = Typography

const SuppliersPage = ({
  data,
  fetching,
  saving,
  filters,
  saveResult,
  supplierDetails,
  supplierDetailsFetching,
  getSuppliers,
  getSupplierDetails,
  setFilters,
  clearSaveResult,
  clearDetails,
  createSupplier,
  updateSupplier,
  deleteSupplier,
  supplierPermissions
}: ConnectedProps<typeof connector>) => {
  const { t } = useTranslation()
  const [showForm, setShowForm] = useState(false)

  const createAllowed = !!supplierPermissions?.supplier_create
  const editAllowed = !!supplierPermissions?.supplier_edit
  const deleteAllowed = !!supplierPermissions?.supplier_delete

  const suppliers = data?.results ?? []
  const count = data?.count ?? 0

  const loadSuppliers = useCallback(() => {
    // Page one, always: `page` is persisted with the filters, and a scrolled
    // list has no way back to the rows a restored page would skip.
    getSuppliers({ ...filters, page: 1 })
  }, [getSuppliers, filters])

  const loadMoreSuppliers = useCallback(() => {
    const url = nextPageUrl(data)
    if (!url) return
    // The server's own url carries the page and every filter it was built
    // with, so nothing here has to reconstruct them.
    getSuppliers({
      ...extractParamsFromNextUrl<SuppliersFilters>(url),
      isMore: true
    })
  }, [data?.next, data?.retryNext, getSuppliers])

  useEffect(() => {
    loadSuppliers()
  }, [loadSuppliers])

  useEffect(() => {
    if (!saveResult) return
    clearSaveResult()
    clearDetails()
    setShowForm(false)
    loadSuppliers()
  }, [saveResult])

  const openCreate = () => {
    clearDetails()
    setShowForm(true)
  }

  const handleSubmit = (values: CreateSupplierData) => {
    if (supplierDetails) {
      updateSupplier({ ...values, id: supplierDetails.id })
    } else {
      createSupplier(values)
    }
  }

  const handleCancel = () => {
    setShowForm(false)
    clearDetails()
  }

  const handleRowAction = (
    record: SupplierListItem,
    action: "edit" | "delete"
  ) => {
    if (action === "edit") {
      getSupplierDetails(record.id)
      setShowForm(true)
      return
    }
    Modal.confirm({
      centered: true,
      title: t("suppliers.deleteTitle"),
      content: t("suppliers.deleteConfirm", { name: record.name }),
      okText: t("suppliers.deleteOk"),
      okButtonProps: { danger: true },
      onOk: () => deleteSupplier(record.id)
    })
  }

  return (
    <div className="grid size-full">
      <Flex vertical gap="middle" className="overflow-auto">
        <Flex justify="space-between" align="center">
          <Flex vertical>
            <Title className="text-start" level={2}>
              {t("suppliers.title")}
            </Title>
            <Text type="secondary">{t("suppliers.subtitle")}</Text>
          </Flex>
          {createAllowed && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("suppliers.addButton")}
            </Button>
          )}
        </Flex>

        <SuppliersList
          selectionFilters={filters}
          suppliers={suppliers}
          search={filters?.q ?? null}
          ordering={filters?.ordering ?? null}
          loading={fetching || saving}
          next={data?.next}
          retryNext={data?.retryNext}
          count={count}
          canEdit={editAllowed}
          canDelete={deleteAllowed}
          onLoadMore={loadMoreSuppliers}
          // A narrower result set has its own first page.
          onSearchChange={(q) => setFilters({ q, page: 1 })}
          onOrderingChange={(ordering) => setFilters({ ordering })}
          onRowAction={handleRowAction}
        />
      </Flex>

      {showForm && (
        <SupplierFormModal
          loading={supplierDetailsFetching}
          saving={saving}
          initialValues={supplierDetails}
          onSave={handleSubmit}
          onCancel={handleCancel}
        />
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => ({
  data: state.suppliers.result,
  fetching: state.suppliers.fetching,
  saving: state.suppliers.saveFetching,
  filters: state.suppliers.filters,
  saveResult: state.suppliers.saveResult,
  supplierDetails: state.suppliers.supplierDetails,
  supplierDetailsFetching: state.suppliers.supplierDetailsFetching,
  supplierPermissions: state?.auth?.permissions?.codes?.suppliers?.suppliers
})

const mapDispatchToProps = {
  getSuppliers: fetchSuppliers,
  getSupplierDetails: fetchSupplierDetails,
  setFilters: suppliersSetFilters,
  clearSaveResult: suppliersClearSaveResult,
  clearDetails: suppliersClearDetails,
  createSupplier,
  updateSupplier,
  deleteSupplier
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(SuppliersPage)
